/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCustomSpec } from '@/utils/hooks/useCustomSpec'
import { Button, ColumnProps, TableLowcode } from 'alurkerja-ui'
import HeaderTableRequest from './HeaderTableRequest'
import TableRequestCell from './TableRequestCell'
import { ChevronLeft, ChevronRight, Eye, XCircle } from 'lucide-react'
import { Requests, TasklistService } from '@/api/tasklist'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useCallback } from 'react'

interface TableRequestProps {
  title: string
  isLoading?: boolean
  data: Requests[]
  page?: string
  processKey?: string
  type: 'active' | 'completed'
}

export default function TableRequest({
  title,
  isLoading = false,
  data = [],
  page = '0',
  processKey = '',
  type,
}: Readonly<TableRequestProps>) {
  const navigate = useNavigate()

  const { refetch } = TasklistService.useGetMyRequest()
  const columns: ColumnProps =
    type === 'active'
      ? [
          { key: 'process_name', label: 'Process' },
          { key: 'business_key', label: 'Request Number' },
          { key: 'start_time', label: 'Request Date' },
          { key: 'name', label: 'Activity' },
          { key: 'assignee', label: 'Assignee' },
        ]
      : [
          { key: 'process_definition_name', label: 'Process' },
          { key: 'start_time', label: 'Request Date' },
          { key: 'end_time', label: 'Completion Date' },
          { key: 'duration', label: 'Duration' },
          { key: 'state', label: 'Status' },
        ]

  const customSpec = useCustomSpec('', {
    columns,
    show_detail: true,
  })

  // handle pagination
  const handlePagination = (pageToPaginanted: number) => {
    navigate(`?processKey=${processKey}&page=${pageToPaginanted}`)
  }

  const renderDetail = useCallback(
    (row: { [x: string]: any }) => <CustomButtonDetail row={row} refetch={refetch} type={type} />,
    [refetch, type],
  )

  return (
    <section className="bg-white rounded pb-10">
      <HeaderTableRequest title={title} />
      <div className="overflow-x-auto">
        <TableLowcode
          data={data}
          spec={customSpec}
          baseUrl={''}
          layout="auto"
          customHeader={() => <></>}
          customCell={TableRequestCell}
          customButtonDetail={renderDetail}
          labelAction="Action"
          isLoadingData={isLoading}
        />
      </div>
      <div className="flex justify-center items-center space-x-4 mt-10">
        <Button
          icon={<ChevronLeft />}
          onClick={() => handlePagination(Number(page) - 1)}
          size="small"
          className="!text-slate-500 disabled:opacity-50 disabled:!text-white bg-slate-100 hover:bg-slate-200"
          disabled={Number(page) === 0}
        ></Button>
        <div>{Number(page) + 1}</div>
        <Button
          className="!text-slate-500 disabled:opacity-50 disabled:!text-white bg-slate-100 hover:bg-slate-200"
          icon={<ChevronRight />}
          onClick={() => handlePagination(Number(page) + 1)}
          size="small"
          disabled={data && data.length < 10}
        ></Button>
      </div>
    </section>
  )
}
export const CustomButtonDetail = ({
  row,
  refetch,
  type,
}: {
  row: { [x: string]: any }
  refetch: () => void
  type: string
}) => {
  const { id, process_definition_id, process_instance_id } = row.props.children[0].props.row
  const navigate = useNavigate()

  function handleCompleteClick() {
    navigate(`completed-prosses/${id}`)
  }

  function handleClick() {
    navigate(`${process_definition_id}/${id}`)
  }

  const { mutate } = TasklistService.useDeleteProcessInstance({
    onSuccess: () => {
      refetch()
    },
  })

  function handleCancelClick() {
    Swal.fire({
      title: 'Konfirmasi',
      text: 'Apakah Anda yakin akan membatalkan proses ini?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Setuju',
      cancelButtonText: 'Batal',
      reverseButtons: true,
    }).then(result => {
      if (result.isConfirmed) {
        mutate({ processInstanceId: process_instance_id })
      }
    })
    // Call mutate to trigger the mutation and delete the process instance
  }

  return (
    <div className="flex flex-row gap-2">
      <Button
        variant="filled"
        className="!text-white-500 hover:bg-slate-200 flex items-center gap-1"
        color="blue"
        size="small"
        onClick={type === 'active' ? handleClick : handleCompleteClick}
      >
        <Eye size={16} />
        Detail
      </Button>
      {type !== 'completed' && (
        <Button
          variant="filled"
          className="!text-white-500 hover:bg-slate-200 flex items-center gap-1"
          color="red"
          size="small"
          onClick={handleCancelClick}
        >
          <XCircle size={16} />
          Cancel
        </Button>
      )}
    </div>
  )
}
